import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, LazyLoadEvent, MessageService } from 'primeng/api';
import { LoadInvoiceGateway } from 'src/app/application/gateway/LoadInvoiceGateway';
import { ProfileGateway } from 'src/app/application/gateway/ProfileGateway';
import { LoadInvoice } from 'src/app/domain/models/LoadInvoice';
import { PageResult } from 'src/app/domain/models/PageResult';
import { ProfileShortInfo } from 'src/app/domain/models/Profile';
import { ProfileUtils } from 'src/app/ui/util/profile-utils';
import { excelUtils } from 'src/app/ui/util/excel-utils';
import { CompanyGateway } from 'src/app/application/gateway/CompanyGateway';
import { Company } from 'src/app/domain/models/Company';

@Component({
  selector: 'app-admin-load-invoice-list',
  templateUrl: './admin-load-invoice-list.component.html',
  styleUrls: ['./admin-load-invoice-list.component.css'],
  providers: [ConfirmationService]
})
export class AdminLoadInvoiceListComponent implements OnInit {

  // Estado
  loading: boolean = false;
  showResults: boolean = false;
  filterForm: FormGroup = new FormGroup({});
  invoices: LoadInvoice[] = [];

  // Paginación
  totalRecords: number = 0;
  pageSize: number = 10;
  pageNumber: number = 1;
  companies: Company[] = [];

  // Opciones de filtros
  profiles: ProfileShortInfo[] = [];
  profileTypeOptions: any[] = ProfileUtils.getProfileTypeOptions();
  selectedProfileType: number = 1;
  byProfile: boolean = true;

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private _profileService: ProfileGateway,
    private _loadInvoiceService: LoadInvoiceGateway,
    private _companyService: CompanyGateway
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.getProfilesByType(1);
  }

  initForm(): void {
    this.filterForm = this.fb.group({
      profileId: [''],
      companyId: ['']
    });
  }

  get canSearch(): boolean {
    const referenceId = this.byProfile ? this.filterForm.get('profileId')?.value : this.filterForm.get('companyId')?.value;
    return !!referenceId;
  }

  onChangeProfileType(type: number): void {
    // Limpiar perfil seleccionado y resultados
    this.filterForm.patchValue({ profileId: '' });
    this.filterForm.patchValue({ companyId: '' });
    this.invoices = [];
    this.totalRecords = 0;

    if (type === 1 || type === 2 || type === 5) {
      this.getProfilesByType(type);
    } else {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('MessageService.Summary.FailedAction'),
        detail: this.translate.instant('MessageService.ProgramsReport.InvalidProfileType')
      });
    }
  }

  getProfilesByType(type: number): void {
    this.loading = true;

    if (type === 5) {
      // Transportistas
      this.getForwarderCompany();
      this.byProfile = false;
    }else if (type === 1) {
      // Exportadores
      this._profileService.getAllExporters().subscribe({
        next: (v) => {
          this.profiles = v.map(exp => ({
            id: exp.id,
            contactName: exp.contactName,
            contactEmail: exp.contactEmail,
            companyName: exp.companyName
          }));
          this.loading = false;
          this.byProfile = true;
        },
        error: (e) => {
          console.log(e);
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: this.translate.instant('MessageService.Profile.ManyRetrieveError')
          });
        }
      });
    } else if (type === 2) {
      // Importadores
      this._profileService.getAllImporters().subscribe({
        next: (v) => {
          this.profiles = v.map(imp => ({
            id: imp.id,
            contactName: imp.contactName,
            contactEmail: imp.contactEmail,
            companyName: imp.companyName
          }));
          this.loading = false;
          this.byProfile = true;
        },
        error: (e) => {
          console.log(e);
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: this.translate.instant('MessageService.Profile.ManyRetrieveError')
          });
        }
      });
    }
  }

  onProfileChange(): void {
    // Limpiar resultados cuando cambia el perfil
    this.invoices = [];
    this.totalRecords = 0;
    this.pageNumber = 1;
  }

  searchInvoices(): void {
    if (!this.canSearch) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('CommonLabels.Warning'),
        detail: this.translate.instant('AdminLoadInvoiceList.SelectProfileWarning')
      });
      return;
    }

    this.pageNumber = 1;
    if(!this.showResults && this.canSearch) {
      this.showResults = true;
    }else{
      this.loadInvoices();
    }
  }

  loadInvoices(event?: LazyLoadEvent): void {
    if (!this.canSearch) {
      return;
    }

    if (event) {
      this.pageNumber = event.first && event.rows ? Math.floor(event.first / event.rows) + 1 : 1;
      this.pageSize = event.rows || 10;
    }

    this.loading = true;
    const referenceId =  this.byProfile ?
    this.filterForm.get('profileId')?.value : this.filterForm.get('companyId')?.value;

    this._loadInvoiceService.getAllInvoicesByReference(
      referenceId,
      this.byProfile,
      this.pageNumber,
      this.pageSize).subscribe({
      next: (response: PageResult<LoadInvoice>) => {
        this.invoices = response.items;
        this.totalRecords = response.totalCount;
        this.loading = false;

        if (this.invoices.length === 0 && this.pageNumber === 1) {
          this.messageService.add({
            severity: 'info',
            summary: this.translate.instant('CommonLabels.Info'),
            detail: this.translate.instant('AdminLoadInvoiceList.NoResultsMessage')
          });
        }
      },
      error: (e) => {
        console.log(e);
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.LoadInvoice.ManyRetrieveError')
        });
      }
    });
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.invoices = [];
    this.totalRecords = 0;
    this.pageNumber = 1;
  }

  getInvoiceName(invoice: LoadInvoice): string {
    return `Proforma_${this.getCompanyName(invoice)}_${invoice.id}`;
  }

  getCompanyName(invoice: LoadInvoice): string {
    if (invoice.profile && invoice.profile.companyName) {
      return invoice.profile.companyName;
    }
    if (invoice.company && invoice.company.companyName) {
      return invoice.company.companyName;
    }
    return 'N.A.';
  }

  downloadExcel(invoice: LoadInvoice): void {
    this.loading = true;

    this._loadInvoiceService.downloadInvoiceExcel(invoice.id).subscribe({
      next: (blob: Blob) => {
        const fileName = `${this.getInvoiceName(invoice)}`;
        excelUtils.downloadExcel(blob, fileName);
        this.loading = false;

        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('MessageService.Summary.SuccessAction'),
          detail: this.translate.instant('AdminLoadInvoiceList.DownloadSuccess')
        });
      },
      error: (e) => {
        console.log(e);
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('UserPages.PackingListView.ErrorDownloadExcel')
        });
      }
    });
  }

  deleteInvoice(invoice: LoadInvoice): void {
    this.confirmationService.confirm({
      message: this.translate.instant('AdminLoadInvoiceList.DeleteConfirmMessage', {
        name: this.getInvoiceName(invoice)
      }),
      header: this.translate.instant('CommonLabels.DeleteConfirmation'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('CommonLabels.Buttons.YesLabel'),
      rejectLabel: this.translate.instant('CommonLabels.Buttons.NoLabel'),
      accept: () => {
        this.loading = true;

        this._loadInvoiceService.deleteInvoice(invoice.id).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant('MessageService.Summary.SuccessAction'),
              detail: this.translate.instant('AdminLoadInvoiceList.DeleteSuccess')
            });

            // Recargar la lista
            this.loadInvoices();
          },
          error: (e) => {
            console.log(e);
            this.loading = false;
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('MessageService.Summary.FailedAction'),
              detail: this.translate.instant('AdminLoadInvoiceList.DeleteError')
            });
          }
        });
      }
    });
  }

  getForwarderCompany()
  {
    this.loading = true;
    this._companyService.getAllByCompanyType(0).subscribe({
      next: (v) => {
        this.companies = v;
        this.byProfile = false;
        this.loading = false;
      },
      error: (e) => {
        console.log(e);
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant(
            'MessageService.Summary.FailedAction',
          ),
          detail: this.translate.instant(
            'MessageService.Company.ManyRetrieveError',
          ),
        });
      }
    });
  }
}
